/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from "react";
import { ToolbarWrapper } from "_metronic/layout/components/toolbar/ToolbarWrapper";
import ModalEntity, { Actions } from "app/modules/tp/components/Modal/ModalEntity";
import BodyWrapper from "app/partials/entity/BodyWrapper";
import Table, { Column, defaultMetaPagination } from "app/partials/entity/Table";
import { Filters, IErrorResponse, IPaginationMeta, IValidationErrorResponse, Messages, Response, Sorters } from "app/modules/tp/_apiResponse";
import Pagination from "app/modules/tp/components/Pagination";
import { Entity } from "app/models/entity";
import { EntityForm } from "app/modules/tp/builders/FormBuilder";
import { createErrorMessage, createSuccessMessage, createWarningMessage } from "app/partials/Alert";
import { Resource, ResourceAction } from "app/infrastructure/repository";
import ValidationError from "app/partials/form/ValidationError";
import _ from "lodash";

type PageWrapperProps<T extends Entity, R extends Entity> = {
    columns: Array<Column<T>>,
    emptyEntity: R,
    entityIcon: string,
    viewApi?: Resource<T>,
    viewDefaultFilters?: Filters,
    viewDefaultSorters?: Sorters,
    entityApi: Resource<R>
    entityApiId?: string,
    labels: {
        icon?: string,
        singleEntityName: string,
        pluralEntityName: string,
    }
    form: EntityForm<R> | Promise<EntityForm<R>>,
    modalActionsHooks?: Actions<R>,
}

function cleanFilters<T extends Entity, F extends (Filters)>(filters: F, tableColumns: Column<T>[], defaultFilters: Filters = {}): Filters {
    const newFilters: Filters = {};
    const hiddenFilters: Filters = {};
    const defaultViewableFilter: Filters = {};

    Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
            delete filters[key];
            return;
        }
        const index = key?.indexOf("[");
        const columnKey = key?.substring(0, index > 0 ? index : key?.length);
        const column = tableColumns.find((column) => column.key === columnKey);
        if (!column) {
            hiddenFilters[key] = filters[key];
            return;
        }
        if (filters[key] !== defaultFilters[key]) {
            newFilters[key] = filters[key];
            return;
        }
        defaultViewableFilter[key] = filters[key];
    });

    if (Object.keys(newFilters)?.length) {
        return { ...defaultViewableFilter, ...newFilters };
    }

    return defaultFilters;
}

function cleanSorters<T extends Entity>(sorters: Sorters, tableColumns: Column<T>[], defaultSorters: Sorters = {}): Sorters {
    const newSorters: Sorters = {};
    const hiddenSorters: Sorters = {};
    const defaultViewableSorter: Sorters = {};

    Object.keys(sorters).forEach((key) => {
        if (!sorters[key]) {
            delete sorters[key];
            return;
        }
        const index = key?.indexOf("[");
        const columnKey = key?.substring(0, index > 0 ? index : key?.length);
        const column = tableColumns.find((column) => column.key === columnKey);
        if (!column) {
            hiddenSorters[key] = sorters[key];
            return;
        }
        if (sorters[key] !== defaultSorters[key]) {
            newSorters[key] = sorters[key];
            return;
        }
        defaultViewableSorter[key] = sorters[key];
    });

    if (Object.keys(newSorters)?.length) {
        return { ...defaultViewableSorter, ...newSorters };
    }

    return defaultSorters;

}


function PageWrapper<T extends Entity, R extends Entity>({ columns, entityIcon, entityApi, entityApiId, viewApi, labels, modalActionsHooks, form, emptyEntity, viewDefaultSorters, viewDefaultFilters, }: PageWrapperProps<T, R>) {
    const [entities, setEntities] = React.useState<Array<T>>([]);
    const [filtersTouched, setFiltersTouched] = React.useState<boolean>(false);
    const [sortersTouched, setSortersTouched] = React.useState<boolean>(false);
    //const [loading, setLoading] = React.useState<boolean>(false);

    const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = React.useState<boolean>(false);
    const [modalAlertMessage, setModalAlertMessage] = React.useState<JSX.Element>(<></>);
    const [alertMessage, setAlertMessage] = React.useState<JSX.Element>(<></>);
    const [modalError, setModalError] = React.useState<JSX.Element>(<></>);

    const [modalEntity, setModalEntity] = React.useState<R>(emptyEntity);

    const [pagination, setPagination] = React.useState<IPaginationMeta>(defaultMetaPagination);
    const [filters, setFilters] = React.useState<any>(viewDefaultFilters ?? {});
    const [sorters, setSorters] = React.useState<any>(viewDefaultSorters ?? {});
    const filterTimeout = React.useRef<any>(null);

    const loading = React.useRef(false);
    const setLoading = (value: boolean) => loading.current = value;

    const closeModals = () => {
        setModalError(<></>);
        setModalAlertMessage(<></>);
        setModalEntity(emptyEntity);
        setShowCreateModal(false);
        setShowUpdateModal(false);
    }

    const onPageChange = (page: number) => {
        setPagination({
            ...pagination,
            current_page: page,
        });
        reloadEntities(page, filters, sorters);
    }

    const withLoading = useCallback(async (f: Function) => {
        if (loading.current) {
            return;
        }
        setLoading(true);
        await f();
        setLoading(false);
    }, [loading]);


    const onTableChange = useCallback(async (page: number = 1, filters: Filters, sorters: Sorters) => {

        if (!filtersTouched && !_.isEqual(filters, viewDefaultFilters)) {
            filters = cleanFilters(filters, columns, viewDefaultFilters);
        }

        if (!sortersTouched && !_.isEqual(sorters, viewDefaultSorters)) {
            sorters = cleanSorters(sorters, columns, viewDefaultSorters);
        }

        if (page !== pagination?.current_page) {
            return reloadEntities(page, filters, sorters);
        }

        if (filterTimeout.current) {
            clearTimeout(filterTimeout.current);
        }
        setFilters(filters);
        setSorters(sorters);
        filterTimeout.current = setTimeout(async () => {
            await reloadEntities(pagination?.current_page, filters, sorters);
            return;
        }, 500);

    }, []);

    const reloadEntities = useCallback(async (page: number = 1, filters: Filters = {}, sorters: Sorters = {}) => {
        (viewApi ?? entityApi).getAllFull({ page, filters, sorters }).then((response) => {
            setEntities((response?.data?.data || []) as Array<T>);
            setPagination(response?.data ?? defaultMetaPagination);
        }).catch(error => {
            setModalAlertMessage(createErrorMessage("Oops!", "Si è verificato un errore durante il caricamento delle informazioni"));
        });
    }, [entityApi]);

    const actionApi = async (action: ResourceAction, entity: R, callback?: ((e: R) => void)) => {
        let res: Response<R> | undefined;
        switch (action) {
            case "create":
                res = await entityApi.create(entity)
                break;
            case "update":
                res = await entityApi.update(entity.id, entity)
                break;
            case "delete":
                res = await entityApi.delete(entity.id)
                break;
            case "duplicate":
                res = await entityApi.duplicate(entity.id)
                break;
            case "reload":
                res = await entityApi.get(entity.id, {})
                break;
            default:
                break;
        }
        if (!(res?.success)) {
            return res;
        }
        callback && res && callback(res?.data as R);
        return res;
    }

    const printModalMessages = (messages: Messages) => {
        return <>
            {messages?.success && (Object.keys(messages?.success)?.length > 0) &&
                createSuccessMessage("Attenzione!", messages)
            }
            {messages?.warning && (Object.keys(messages?.warning)?.length > 0) &&
                createWarningMessage("Attenzione!", messages)
            }
            {messages?.error && (Object.keys(messages?.error)?.length > 0) &&
                createErrorMessage("Oops!", messages)
            }
        </>
    }

    const actionAndUpdateView = async (action: ResourceAction | "save", entity: R) => {
        if (action === "save") {
            action = (entity.id ? "update" : "create");
        }
        const res = await actionApi(action, entity, (_: Entity) => {
            withLoading(async () => await reloadEntities(pagination?.current_page, filters, sorters));
        })
        console.log("actionAPI", res);
        if (res?.success === false) {
            if ((res as IErrorResponse)?.type === "foreign_relation") {
                return setModalError(createWarningMessage("Oops!", "Non è possibile eliminare la risorsa perché è collegata ad altre entità"));
            }
            if ((res as IErrorResponse)?.type === "validation") {
                return setModalError(ValidationError({ error: (res as IValidationErrorResponse) }));
            }
        }
        if (res?.success) {
            if (action === 'create') {
                setShowCreateModal(false);
                setShowUpdateModal(true);
            }
            setModalEntity(res?.data as R);
            const successString = ((typeof res?.message === "string") && res.message) || "L'operazione è stata completata con successo";
            let modalMessages = createSuccessMessage("Operazione completata!", successString);
            const messages: Messages | false = (typeof res?.message === "object") && res.message;
            if (messages) {
                modalMessages = <>{modalMessages}{printModalMessages(messages)}</>;
            }
            setModalAlertMessage(modalMessages);
        } else {
            setModalError(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
            //setAlertMessage(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
        }

        return res;
    }

    const modalActions: Actions<R> = {
        save: {
            label: "Salva",
            onClick: async (entity: R) => {
                console.log("modalAction", entity);
                return (!!((await actionAndUpdateView("save", entity))?.success || (await modalActionsHooks?.save?.onClick(entity))));
            }
        },
        destroy: {
            label: "Elimina",
            onClick: async (entity: R) => {
                if (!!((await actionAndUpdateView("delete", entity))?.success || modalActionsHooks?.destroy?.onClick(entity))) {
                    closeModals();
                    setAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
                    return true;
                }
                return false;
            }
        },
        cancel: {
            label: "Chiudi",
            onClick: async (entity: R) => {
                closeModals();
                modalActionsHooks?.cancel?.onClick(entity);
                return true;
            }
        },
        duplicate: {
            label: "",
            onClick: async (entity: R) => {
                return (!!((await actionAndUpdateView("duplicate", entity))?.success || modalActionsHooks?.duplicate?.onClick(entity)));
            }
        },
        reload: {
            label: "",
            onClick: async (entity: R) => {
                return (!!((await actionAndUpdateView("reload", entity))?.success || modalActionsHooks?.duplicate?.onClick(entity)));
            }
        },
    }

    //React.useEffect(() => {
    //    withLoading(async () => await reloadEntities(1, filters, sorters));
    //}, [withLoading, reloadEntities, filters, sorters])

    React.useEffect(() => {
        withLoading(async () => await reloadEntities(pagination?.current_page, filters, sorters));
    }, [withLoading, reloadEntities])

    return (
        <>
            {
                showCreateModal &&
                <ModalEntity<R>
                    title={{
                        icon: labels?.icon,
                        label: `Aggiungi ${labels.singleEntityName}`
                    }}
                    initialEntity={modalEntity}
                    actions={modalActions}
                    eform={form}
                    show={showCreateModal}
                    error={modalError}
                    message={modalAlertMessage}
                />
            }
            {
                showUpdateModal &&
                <ModalEntity<R>
                    key={modalEntity?.id}
                    title={{
                        icon: labels?.icon,
                        label: `Scheda ${labels.singleEntityName}`
                    }}
                    actions={modalActions}
                    eform={form}
                    show={showUpdateModal}
                    initialEntity={modalEntity}
                    error={modalError}
                    message={modalAlertMessage}
                />
            }
            <ToolbarWrapper
                items={[
                    {
                        display: true,
                        component: {
                            type: 'button',
                            label: `Aggiungi ${labels.singleEntityName}`,
                            icon: 'fa-solid fa-plus',
                            onClick: () => setShowCreateModal(true)
                        }
                    }
                ]}
            />
            {
                alertMessage
            }
            <BodyWrapper
                title={{
                    icon: entityIcon,
                    label: labels?.pluralEntityName?.toUpperCase()
                }}
                footer={
                    // @TODO: move Pagination in Table component
                    <Pagination data={pagination} changePage={onPageChange} />
                }
            >
                <Table
                    entities={entities}
                    columns={columns}
                    filters={filters}
                    sorters={sorters}
                    onChange={onTableChange}
                    pagination={{
                        current_page: pagination?.current_page,
                        last_page: pagination?.last_page,
                        per_page: pagination?.per_page,
                        total: pagination?.total,
                    }}
                    onRowClick={async (entity: T) => {
                        const actionEntity = (await entityApi.getFull(entity[entityApiId ?? "id"], {}))?.data;
                        setModalEntity(actionEntity);
                        setShowUpdateModal(true);
                    }}
                />
            </BodyWrapper>
        </>
    );
};


export default PageWrapper;
